import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import SelectUI from '../../components/ui/Select';
import Icon from '../../components/AppIcon';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (n) =>
  new Intl.NumberFormat('fr-CI', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 })?.format(n || 0);

const formatDate = (d) =>
  d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const APP_STATUS = {
  draft:        { label: 'Brouillon',       color: 'bg-muted text-muted-foreground border-border' },
  submitted:    { label: 'Soumise',          color: 'bg-blue-50 text-blue-700 border-blue-200' },
  under_review: { label: 'En révision',      color: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved:     { label: 'Approuvée',        color: 'bg-green-50 text-green-700 border-green-200' },
  rejected:     { label: 'Rejetée',          color: 'bg-red-50 text-red-700 border-red-200' },
};

const BOOKING_STATUS = {
  pending:     { label: 'En attente',  color: 'bg-amber-50 text-amber-700 border-amber-200' },
  confirmed:   { label: 'Confirmé',    color: 'bg-blue-50 text-blue-700 border-blue-200' },
  in_progress: { label: 'En cours',    color: 'bg-purple-50 text-purple-700 border-purple-200' },
  completed:   { label: 'Terminé',     color: 'bg-green-50 text-green-700 border-green-200' },
  cancelled:   { label: 'Annulé',      color: 'bg-red-50 text-red-700 border-red-200' },
};

// ─── Profile Card ─────────────────────────────────────────────────────────────

const ProfileCard = ({ professional, application }) => {
  const source = professional || application;
  if (!source) return null;

  const name = source?.full_name || source?.fullName || '—';
  const category = source?.category || '—';
  const email = source?.email || '—';
  const phone = source?.phone || source?.whatsapp || '—';
  const city = source?.city || source?.commune || '—';
  const bio = source?.bio || null;
  const commissionRate = professional?.commission_rate ?? application?.commissionRate ?? null;
  const isVerified = professional?.is_verified || false;
  const status = professional?.status || 'pending';
  const avatarUrl = source?.avatar_url || source?.avatarUrl || null;
  const yearsExp = source?.years_experience || source?.yearsExperience || null;
  const specialty = source?.specialty || null;
  const tags = source?.tags || [];
  const langs = source?.languages || [];

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header band */}
      <div className="h-20 bg-gradient-to-r from-brand-earth via-brand-earth/80 to-brand-gold relative">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }}
        />
      </div>

      <div className="px-5 pb-5">
        {/* Avatar + badges */}
        <div className="flex items-end justify-between -mt-8 mb-4">
          <div className="relative">
            {avatarUrl ? (
              <img src={avatarUrl} alt={name} className="w-16 h-16 rounded-lg border-4 border-card shadow-sm object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-lg border-4 border-card shadow-sm bg-gradient-to-br from-brand-earth to-brand-gold flex items-center justify-center">
                <span className="text-white text-xl font-semibold">
                  {name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
            )}
            {isVerified && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center border-2 border-card">
                <Icon name="Check" size={10} color="white" />
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2 mb-1">
            <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${
              status === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
              status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-muted text-muted-foreground border-border'
            }`}>
              {status === 'active' ? '● Actif' : status === 'pending' ? '● En attente' : status}
            </span>
          </div>
        </div>

        {/* Name & category */}
        <h2 className="text-lg font-semibold text-foreground leading-tight">{name}</h2>
        <p className="text-sm text-brand-earth font-medium capitalize mt-0.5">{category}</p>
        {specialty && <p className="text-xs text-muted-foreground mt-0.5">{specialty}</p>}

        {/* Commission rate highlight */}
        {commissionRate != null && (
          <div className="mt-3 flex items-center space-x-2 px-3 py-2 bg-muted/40 border border-border rounded-md">
            <Icon name="Percent" size={14} className="text-brand-earth flex-shrink-0" />
            <span className="text-xs text-muted-foreground">Taux de commission :</span>
            <span className="text-sm font-semibold text-brand-earth">{commissionRate}%</span>
          </div>
        )}

        {/* Info grid */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          {[
            { icon: 'Mail', value: email },
            { icon: 'Phone', value: phone },
            { icon: 'MapPin', value: city },
            { icon: 'Award', value: yearsExp ? `${yearsExp} ans d'exp.` : null },
          ]?.filter(i => i?.value)?.map((item, idx) => (
            <div key={idx} className="flex items-center space-x-2 p-2.5 bg-muted/40 border border-border/50 rounded-md">
              <Icon name={item?.icon} size={13} className="text-muted-foreground flex-shrink-0" />
              <span className="text-xs text-foreground truncate">{item?.value}</span>
            </div>
          ))}
        </div>

        {/* Bio */}
        {bio && (
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed line-clamp-3">{bio}</p>
        )}

        {/* Tags */}
        {tags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {tags?.slice(0, 5)?.map((tag, i) => (
              <span key={i} className="px-2.5 py-1 bg-muted border border-border rounded-md text-xs font-medium text-muted-foreground capitalize">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Languages */}
        {langs?.length > 0 && (
          <div className="mt-3 flex items-center space-x-2">
            <Icon name="Globe" size={13} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{langs?.join(', ')}</span>
          </div>
        )}

        {/* Action buttons */}
        <div className="mt-4 pt-4 border-t border-border space-y-2">
          <Button variant="outline" size="sm" fullWidth asChild>
            <Link to="/professional-dashboard">
              <Icon name="Edit3" size={14} className="mr-1.5" />
              Modifier mon profil
            </Link>
          </Button>
          <Button variant="elegant" size="sm" fullWidth asChild>
            <Link to="/professional-onboarding">
              <Icon name="Sparkles" size={14} className="mr-1.5" />
              Assistant de configuration
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Active Applications ──────────────────────────────────────────────────────

const ActiveApplications = ({ userId }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    const fetch = async () => {
      setLoading(true);
      const { data } = await supabase
        ?.from('professional_applications')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false });
      setApps(data || []);
      setLoading(false);
    };
    fetch();
  }, [userId]);

  const active = apps?.filter(a => ['submitted', 'under_review', 'approved']?.includes(a?.application_status));

  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center">
            <Icon name="FileText" size={16} className="text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Candidatures actives</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{active?.length} en cours</p>
          </div>
        </div>
        <Button variant="outline" size="xs" asChild>
          <Link to="/professional-signup">
            <Icon name="Plus" size={12} className="mr-1" />
            Nouvelle
          </Link>
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : apps?.length === 0 ? (
        <div className="text-center py-10">
          <div className="w-12 h-12 bg-muted/40 border border-border rounded-md flex items-center justify-center mx-auto mb-3">
            <Icon name="FilePlus" size={20} className="text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground mb-1">Aucune candidature</p>
          <p className="text-xs text-muted-foreground">Soumettez votre première candidature pour rejoindre le réseau.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {apps?.map(app => {
            const st = APP_STATUS?.[app?.application_status] || APP_STATUS?.draft;
            return (
              <div key={app?.id} className="p-3.5 bg-muted/40 rounded-md border border-border hover:border-border/80 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <p className="text-sm font-semibold text-foreground truncate capitalize">{app?.category}</p>
                      {app?.commission_rate != null && (
                        <span className="flex items-center space-x-1 px-2 py-0.5 bg-muted border border-border rounded-md text-xs font-medium text-muted-foreground">
                          <Icon name="Percent" size={10} />
                          <span>{app?.commission_rate}%</span>
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{app?.full_name} · {app?.email}</p>
                    <div className="flex items-center space-x-3 mt-1.5">
                      <span className="flex items-center space-x-1 text-xs text-muted-foreground">
                        <Icon name="Calendar" size={11} />
                        <span>Soumise le {formatDate(app?.submitted_at || app?.created_at)}</span>
                      </span>
                    </div>
                    {app?.rejection_reason && app?.application_status === 'rejected' && (
                      <div className="mt-2 p-2.5 bg-red-50 border border-red-100 rounded-md">
                        <p className="text-xs text-red-600 italic">"{app?.rejection_reason}"</p>
                      </div>
                    )}
                  </div>
                  <span className={`ml-3 flex-shrink-0 px-2.5 py-1 rounded-md text-xs font-semibold border ${st?.color}`}>
                    {st?.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Commission Tracking ──────────────────────────────────────────────────────

const CommissionTracking = ({ professional }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  const commissionRate = professional?.commission_rate != null ? parseFloat(professional?.commission_rate) / 100 : null;
  const dailyRate = parseFloat(professional?.daily_rate || 0);
  const hourlyRate = parseFloat(professional?.hourly_rate || 0);
  const ratePerJob = dailyRate || hourlyRate * 8 || 0;

  useEffect(() => {
    if (!professional?.id) return;
    const fetch = async () => {
      setLoading(true);
      const { data } = await supabase
        ?.from('professional_bookings')
        ?.select('*')
        ?.eq('professional_id', professional?.id)
        ?.eq('booking_status', 'completed')
        ?.order('preferred_date', { ascending: false });
      setBookings(data || []);
      setLoading(false);
    };
    fetch();
  }, [professional?.id]);

  const now = new Date();
  const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

  const filterByPeriod = (b) => {
    const d = new Date(b?.preferred_date);
    if (period === 'month') return d?.getMonth() === now?.getMonth() && d?.getFullYear() === now?.getFullYear();
    if (period === 'quarter') return Math.floor(d?.getMonth() / 3) === Math.floor(now?.getMonth() / 3) && d?.getFullYear() === now?.getFullYear();
    return d?.getFullYear() === now?.getFullYear();
  };

  const periodBookings = bookings?.filter(filterByPeriod);
  const grossRevenue = periodBookings?.length * ratePerJob;
  const commissionOwed = commissionRate != null ? grossRevenue * commissionRate : null;
  const netRevenue = commissionOwed != null ? grossRevenue - commissionOwed : grossRevenue;

  const monthlyData = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const count = bookings?.filter(b => {
      const bd = new Date(b?.preferred_date);
      return bd?.getMonth() === d?.getMonth() && bd?.getFullYear() === d?.getFullYear();
    })?.length;
    const gross = count * ratePerJob;
    const commission = commissionRate != null ? gross * commissionRate : 0;
    return { month: MONTHS_FR?.[d?.getMonth()], count, gross, commission, net: gross - commission };
  });

  const maxGross = Math.max(...monthlyData?.map(m => m?.gross), 1);

  const periodOptions = [
    { value: 'month', label: 'Mois' },
    { value: 'quarter', label: 'Trimestre' },
    { value: 'year', label: 'Année' },
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-md bg-green-50 border border-green-100 flex items-center justify-center">
            <Icon name="TrendingUp" size={16} className="text-green-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Suivi des commissions</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {commissionRate != null ? `Taux : ${professional?.commission_rate}%` : 'Taux non défini'}
            </p>
          </div>
        </div>
        <div className="w-36">
          <SelectUI
            options={periodOptions}
            value={period}
            onChange={(val) => setPeriod(val)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="p-3.5 bg-muted/40 border border-border rounded-md text-center">
              <p className="text-xs text-muted-foreground font-medium mb-1">Missions</p>
              <p className="text-xl font-semibold text-foreground">{periodBookings?.length}</p>
            </div>
            <div className="p-3.5 bg-green-50 border border-green-100 rounded-md text-center">
              <p className="text-xs text-muted-foreground font-medium mb-1">Revenu net</p>
              <p className="text-sm font-semibold text-green-700 truncate">
                {ratePerJob > 0 ? formatCurrency(netRevenue) : '—'}
              </p>
            </div>
            <div className="p-3.5 bg-amber-50 border border-amber-100 rounded-md text-center">
              <p className="text-xs text-muted-foreground font-medium mb-1">Commission</p>
              <p className="text-sm font-semibold text-amber-700 truncate">
                {commissionOwed != null && ratePerJob > 0 ? formatCurrency(commissionOwed) : '—'}
              </p>
            </div>
          </div>

          {/* Bar chart */}
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Revenus / 6 derniers mois</p>
            <div className="flex items-end space-x-2 h-28">
              {monthlyData?.map((m, i) => (
                <div key={i} className="flex-1 flex flex-col items-center space-y-1">
                  <span className="text-xs text-muted-foreground font-medium">{m?.count > 0 ? m?.count : ''}</span>
                  <div className="w-full flex items-end justify-center" style={{ height: '72px' }}>
                    <div className="w-full relative rounded-t-md overflow-hidden" style={{ height: `${Math.max((m?.gross / maxGross) * 100, m?.gross > 0 ? 8 : 2)}%`, minHeight: '3px' }}>
                      <div className="absolute inset-0 bg-gradient-to-t from-green-500 to-green-300" />
                      {commissionRate != null && m?.gross > 0 && (
                        <div
                          className="absolute bottom-0 left-0 right-0 bg-amber-400/60"
                          style={{ height: `${commissionRate * 100}%` }}
                        />
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{m?.month}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center space-x-4 mt-2">
              <span className="flex items-center space-x-1.5 text-xs text-muted-foreground">
                <span className="w-3 h-3 bg-green-400 rounded-sm" />
                <span>Revenu brut</span>
              </span>
              {commissionRate != null && (
                <span className="flex items-center space-x-1.5 text-xs text-muted-foreground">
                  <span className="w-3 h-3 bg-amber-400/60 rounded-sm" />
                  <span>Commission Kapital</span>
                </span>
              )}
            </div>
          </div>

          {/* Recent completed */}
          {periodBookings?.length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Missions récentes</p>
              <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                {periodBookings?.slice(0, 6)?.map(b => {
                  const gross = ratePerJob;
                  const comm = commissionRate != null ? gross * commissionRate : null;
                  return (
                    <div key={b?.id} className="flex items-center justify-between p-3 bg-muted/40 rounded-md border border-border">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{b?.client_name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{b?.service_type?.replace(/_/g, ' ')}</p>
                      </div>
                      <div className="text-right ml-3 flex-shrink-0 space-y-0.5">
                        <p className="text-xs text-muted-foreground">{formatDate(b?.preferred_date)}</p>
                        {gross > 0 && <p className="text-xs font-semibold text-green-600">{formatCurrency(gross)}</p>}
                        {comm != null && gross > 0 && <p className="text-xs text-amber-600">-{formatCurrency(comm)}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Scheduled Appointments ───────────────────────────────────────────────────

const ScheduledAppointments = ({ professional }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [updating, setUpdating] = useState(null);

  const fetchBookings = useCallback(async () => {
    if (!professional?.id) return;
    setLoading(true);
    const { data } = await supabase
      ?.from('professional_bookings')
      ?.select('*')
      ?.eq('professional_id', professional?.id)
      ?.order('preferred_date', { ascending: true });
    setBookings(data || []);
    setLoading(false);
  }, [professional?.id]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateStatus = async (id, status) => {
    setUpdating(id);
    await supabase?.from('professional_bookings')?.update({ booking_status: status })?.eq('id', id);
    await fetchBookings();
    setUpdating(null);
  };

  const today = new Date();
  today?.setHours(0, 0, 0, 0);

  const filtered = bookings?.filter(b => {
    const d = new Date(b?.preferred_date);
    if (filter === 'upcoming') return d >= today && ['pending', 'confirmed']?.includes(b?.booking_status);
    if (filter === 'today') {
      const t = new Date();
      return d?.toDateString() === t?.toDateString();
    }
    if (filter === 'past') return d < today || b?.booking_status === 'completed';
    return true;
  });

  const filterTabs = [
    { k: 'upcoming', l: 'À venir' },
    { k: 'today', l: "Aujourd\'hui" },
    { k: 'past', l: 'Passés' },
    { k: 'all', l: 'Tous' },
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-md bg-purple-50 border border-purple-100 flex items-center justify-center">
            <Icon name="CalendarClock" size={16} className="text-purple-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Rendez-vous planifiés</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{bookings?.filter(b => ['pending','confirmed']?.includes(b?.booking_status))?.length} à venir</p>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center space-x-1 bg-muted/40 border border-border rounded-md p-1 mb-5">
        {filterTabs?.map(f => (
          <button
            key={f?.k}
            onClick={() => setFilter(f?.k)}
            className={`flex-1 py-1.5 rounded-sm text-xs font-medium transition-all duration-200 ${
              filter === f?.k
                ? 'bg-card border border-border text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {f?.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered?.length === 0 ? (
        <div className="text-center py-10">
          <div className="w-12 h-12 bg-muted/40 border border-border rounded-md flex items-center justify-center mx-auto mb-3">
            <Icon name="CalendarX" size={20} className="text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">Aucun rendez-vous trouvé</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered?.map(booking => {
            const st = BOOKING_STATUS?.[booking?.booking_status] || BOOKING_STATUS?.pending;
            const isToday = new Date(booking?.preferred_date)?.toDateString() === new Date()?.toDateString();
            return (
              <div
                key={booking?.id}
                className={`p-4 rounded-md border transition-colors ${
                  isToday ? 'bg-purple-50/60 border-purple-200' : 'bg-muted/40 border-border hover:border-border/80'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 min-w-0 flex-1">
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${isToday ? 'bg-purple-100' : 'bg-muted border border-border'}`}>
                      <Icon name="User" size={15} className={isToday ? 'text-purple-600' : 'text-muted-foreground'} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-semibold text-foreground truncate">{booking?.client_name}</p>
                        {isToday && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 border border-purple-200 rounded-md text-xs font-semibold flex-shrink-0">Aujourd'hui</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 capitalize">{booking?.service_type?.replace(/_/g, ' ')}</p>
                      <div className="flex items-center space-x-3 mt-1.5 flex-wrap gap-y-1">
                        <span className="flex items-center space-x-1 text-xs text-muted-foreground">
                          <Icon name="Calendar" size={11} />
                          <span>{formatDate(booking?.preferred_date)}</span>
                        </span>
                        {booking?.preferred_time && (
                          <span className="flex items-center space-x-1 text-xs text-muted-foreground">
                            <Icon name="Clock" size={11} />
                            <span>{booking?.preferred_time}</span>
                          </span>
                        )}
                        {booking?.client_phone && (
                          <span className="flex items-center space-x-1 text-xs text-muted-foreground">
                            <Icon name="Phone" size={11} />
                            <span>{booking?.client_phone}</span>
                          </span>
                        )}
                      </div>
                      {booking?.notes && (
                        <p className="mt-1.5 text-xs text-muted-foreground italic line-clamp-2">"{booking?.notes}"</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-2 ml-3 flex-shrink-0">
                    <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${st?.color}`}>
                      {st?.label}
                    </span>
                    {booking?.booking_status === 'pending' && (
                      <div className="flex items-center space-x-1.5">
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() => updateStatus(booking?.id, 'confirmed')}
                          disabled={updating === booking?.id}
                          className="text-green-700 border-green-200 hover:bg-green-50"
                        >
                          Confirmer
                        </Button>
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() => updateStatus(booking?.id, 'cancelled')}
                          disabled={updating === booking?.id}
                          className="text-red-700 border-red-200 hover:bg-red-50"
                        >
                          Refuser
                        </Button>
                      </div>
                    )}
                    {booking?.booking_status === 'confirmed' && (
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() => updateStatus(booking?.id, 'completed')}
                        disabled={updating === booking?.id}
                        className="text-blue-700 border-blue-200 hover:bg-blue-50"
                      >
                        Terminé
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Main Portal ──────────────────────────────────────────────────────────────

const ProfessionalPortal = () => {
  const { user } = useAuth();
  const [professional, setProfessional] = useState(null);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    const init = async () => {
      setLoading(true);
      const [profResult, appResult] = await Promise.all([
        supabase?.from('professionals')?.select('*')?.or(`email.eq.${user?.email},created_by.eq.${user?.id}`)?.limit(1)?.single(),
        supabase?.from('professional_applications')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(1)?.single(),
      ]);
      if (profResult?.data) setProfessional(profResult?.data);
      if (appResult?.data) setApplication(appResult?.data);
      setLoading(false);
    };
    init();
  }, [user?.id, user?.email]);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="w-16 h-16 bg-muted/40 border border-border rounded-lg flex items-center justify-center mb-4">
            <Icon name="Lock" size={26} className="text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Connexion requise</h2>
          <p className="text-sm text-muted-foreground text-center mb-6 max-w-sm">
            Connectez-vous pour accéder à votre espace professionnel.
          </p>
          <Button variant="default" size="lg" asChild>
            <Link to="/auth">Se connecter</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isApproved = professional?.status === 'active' || application?.application_status === 'approved';

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Espace Professionnel — Kapital States</title>
        <meta name="description" content="Consultez votre profil, candidatures actives, commissions et rendez-vous planifiés." />
      </Helmet>
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page header */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center space-x-4">
            <div className="w-11 h-11 bg-gradient-to-br from-brand-gold to-brand-earth rounded-lg flex items-center justify-center">
              <Icon name="UserCheck" size={20} color="white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground leading-tight">
                Espace Professionnel
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {loading ? 'Chargement…' : professional?.full_name || application?.full_name || user?.email}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/professional-inbox">
                <Icon name="Inbox" size={14} className="mr-1.5" />
                <span className="hidden sm:inline">Boîte de réception</span>
              </Link>
            </Button>
            {!loading && isApproved && (
              <span className="hidden sm:flex items-center space-x-1.5 px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-md text-xs font-semibold">
                <Icon name="BadgeCheck" size={13} />
                <span>Professionnel approuvé</span>
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-9 h-9 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-sm text-muted-foreground">Chargement de votre espace…</p>
          </div>
        ) : !professional && !application ? (
          <div className="bg-card border border-border rounded-lg p-10 text-center max-w-lg mx-auto">
            <div className="w-14 h-14 bg-muted/40 border border-border rounded-lg flex items-center justify-center mx-auto mb-4">
              <Icon name="UserPlus" size={24} className="text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">
              Aucun profil trouvé
            </h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              Rejoignez le réseau de professionnels Kapital States pour accéder à votre espace dédié.
            </p>
            <Button variant="elegant" size="lg" asChild>
              <Link to="/professional-signup">
                <Icon name="UserPlus" size={16} className="mr-1.5" />
                Devenir Prestataire
              </Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column — Profile */}
            <div className="lg:col-span-1 space-y-5">
              <ProfileCard professional={professional} application={application} />
              <ActiveApplications userId={user?.id} />
            </div>

            {/* Right column — Commission + Appointments */}
            <div className="lg:col-span-2 space-y-5">
              {professional ? (
                <>
                  <CommissionTracking professional={professional} />
                  <ScheduledAppointments professional={professional} />
                </>
              ) : (
                <div className="bg-card border border-border rounded-lg p-8 text-center">
                  <div className="w-14 h-14 bg-amber-50 border border-amber-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="Clock" size={24} className="text-amber-600" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">
                    Candidature en cours d'examen
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-4">
                    Votre candidature est en cours de révision. Une fois approuvée, vous aurez accès au suivi des commissions et à la gestion des rendez-vous.
                  </p>
                  {application && (
                    <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted/40 border border-border rounded-md">
                      <Icon name="FileText" size={14} className="text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground capitalize">{application?.category}</span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className={`px-2 py-0.5 rounded-md text-xs font-semibold border ${APP_STATUS?.[application?.application_status]?.color}`}>
                        {APP_STATUS?.[application?.application_status]?.label}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessionalPortal;
